<template>
  <div>
    <FormLabel
      v-if="label"
      :label="label"
      :label-hint="labelHint"
      :required="required"
      :for="name"
    />
    <div class="relative w-full z-[100]">
      <FormInput
        :autocomplete="autocomplete"
        :input-classes="inputClasses"
        :input-styles="inputStyles"
        :model-value="computedModelValue"
        :tab-index="tabIndex"
        :name="name"
        autofill="off"
        type="search"
        @update:modelValue="updateSearch"
        :placeholder="placeholder"
        @input-focus="setOpen(true)"
        ref="searchHolder"
        data-lpignore="true"
      />
      <button
        v-if="!show && modelValue"
        type="button"
        aria-label="Clear Input"
        class="absolute top-1/2 -translate-y-1/2 right-12 z-max-2 text-slate-400 hover:text-slate-600 p-1"
        @click="updateModelValue('')"
      >
        <XMarkIcon
          aria-hidden="true"
          class="w-5 h-5"
        />
      </button>
      <TransitionFade>
        <div
          v-show="show"
          :id="`${name || id}-dropdown`"
          class="absolute top-[2.7rem] right-0 left-0 -mt-px flex w-full flex-col rounded-lg border border-slate-200 bg-white focus:outline-none dark:border-gray-600 dark:bg-slate-800"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="menu-button"
          tabindex="-1">
          <div class="fixed inset-0 -z-10" @click="show = false" tabindex="-1"/>
          <div v-if="searchResults?.length"
               :id="`${name || id}-dropdown-options`"
               class="overflow-y-auto px-2 shadow-lg max-h-[200px]">
            <button type="button" v-for="(option,index) in searchResults" :key="index"
                    class="flex w-full cursor-pointer select-none items-center gap-4 rounded px-3 py-1 text-left"
                    :class="isSelected(option) ? 'bg-primary-100 text-primary-600 font-medium' : 'hover:bg-slate-50 text-slate-900'"
                    @click="updateModelValue(option)">
              <span>{{ getOptionName(option) }}</span>
            </button>
            <InfiniteScrollTrigger @trigger="() => handleSearch(currentPage+1)"/>
            <AnimationLoadingDotsHorizontal v-if="loading" class="mx-auto block"/>
          </div>
          <div v-else class="text-center text-sm text-slate-400">
            <div class="p-6 space-y-6">
              <slot name="empty">
                <p>No results found</p>
              </slot>
              <FormButton v-if="allowSupportRequest && store.isLoggedIn" size="md"
                          @click="openSupportRequest" color="secondary">
                Request a new option →
              </FormButton>
            </div>
          </div>
        </div>
      </TransitionFade>
    </div>
    <SupportRequestModal v-model:open="showSupportRequestModal" :type="supportRequestType" :meta="{search}"/>
  </div>
</template>

<script setup>
import FormLabel from "./FormLabel.vue";
import FormInput from "./FormInput.vue";
import InfiniteScrollTrigger from "./InfiniteScrollTrigger.vue";
import AnimationLoadingDotsHorizontal from "./AnimationLoadingDotsHorizontal.vue";
import FormButton from "./FormButton.vue";
import SupportRequestModal from "./SupportRequestModal.vue";
import TransitionFade from "./TransitionFade.vue";
import {computed, nextTick, onMounted, onUnmounted, ref} from "vue";
import {debounce} from "lodash";
import {XMarkIcon} from "@heroicons/vue/24/outline";
import {useAccountStore} from "../stores/AccountStore.js";

const props = defineProps({
  modelValue: [String, Number, Object],
  label: String,
  labelHint: String,
  getOptions: Function,
  placeholder: {
    type: String,
    default: "Select",
  },
  name: String,
  allowSupportRequest: Boolean,
  supportRequestType: String,
  autocomplete: String,
  required: Boolean,
  inputStyles: String,
  inputClasses: String,
  tabIndex: String,
});

const emits = defineEmits(["update:modelValue"]);
const show = ref(false);
const searchHolder = ref(null);
const showSupportRequestModal = ref(false);
const store = useAccountStore();

// (event) close dropdown when typing esc button.
const handleCloseOnEscape = (event) => {
  if (["Escape", "Esc"].includes(event.key)) {
    show.value = false;
  }
};

const id = "el" + Math.random().toString(36).substring(7);

const openSupportRequest = () => showSupportRequestModal.value = true;

const updateModelValue = (value) => {
  emits("update:modelValue", value);
  search.value = typeof value === "string" ? value : value?.name;
  show.value = false;
};

const search = ref("");
const searchResults = ref([]);
const currentPage = ref(1);
const loading = ref(false);

const handleSearch = (page = 1) => {
  if (isNaN(page)) page = 1;

  loading.value = true;
  props.getOptions({page: page, search: search.value}).then((response) => {
    if (page === 1) searchResults.value = [];

    const items = response.data.map((item) => {
      if (Object.keys(item).length === 1 && item?.name) return item.name;
      return item;
    });

    searchResults.value.push(...items);
    currentPage.value = response.meta.current_page ?? 1;
  }).finally(() => {
    loading.value = false;
  });
};

const debounceHandleSearch = debounce(handleSearch, 300);

const updateSearch = (value) => {
  if (!show.value) show.value = !!value;
  search.value = value;
  debounceHandleSearch();
};

const computedModelValue = computed(() => {
  if (show.value) return search.value;
  return selectedOption.value;
});

const selectedOption = computed(() => {
  return typeof props.modelValue === "string" ? props.modelValue : "";
});

const setOpen = async (value) => {
  show.value = value;

  if (show.value) {
    await nextTick();
    if (!searchHolder.value) return;
    searchHolder.value.inputEl.focus();
  }
};

const isSelected = (option) => {
  if (typeof option === "string") return props.option === props.modelValue;
  return props.option?.name === props.modelValue;
};

const getOptionName = (option) => {
  if (typeof option === "string") return option;
  return option.name;
};

onMounted(() => handleSearch());

onMounted(() => {
  document.addEventListener("keydown", handleCloseOnEscape);
});

onUnmounted(() => {
  // remove handleCloseOnEscape when unmounting the component.
  document.removeEventListener("keydown", handleCloseOnEscape);
});
</script>

<style scoped>
.input-y-padding-exact {
  padding-top: 0.475rem;
  padding-bottom: 0.475rem;
}
</style>
